/**
 * AppICEMAP — Hash Router
 * Lightweight hash-based router (~200 LOC).
 *
 * Supports:
 *   - Route patterns with params (`#/detail/:id`)
 *   - Wildcard fallback (`*`)
 *   - beforeEach guards (async)
 *   - navigate(path) + replace
 *   - onChange callback
 *
 * Route definition:
 *   { pattern: string, handler: (params: object) => void }
 *
 * Usage:
 *   router.on('/', () => { ... })
 *   router.on('/detail/:id', (params) => { ... })
 *   router.navigate('/detail/abc-123')
 */

/** @type {Array<{ pattern: string, regex: RegExp, paramNames: string[], handler: Function }>} */
const routes = [];

/** @type {Array<{ (...args): boolean | Promise<boolean> }>} */
const guards = [];

/** @type {Array<{ (route: string, params: object): void }>} */
const changeListeners = [];

/** Current resolved route path (without hash) */
let currentRoute = "";

/* ── Route registration ──────────────────────────────────────────────────── */

/**
 * Register a route handler.
 * @param {string} pattern - e.g. "/" or "/detail/:id"
 * @param {Function} handler - called with (params: object)
 * @returns {Router} for chaining
 */
export function on(pattern, handler) {
	const paramNames = [];
	const regexStr = pattern
		.replace(/\//g, "\\/")
		.replace(/:(\w+)/g, (_match, name) => {
			paramNames.push(name);
			return "([^/]+)";
		})
		.replace(/\*/g, ".*");

	routes.push({
		pattern,
		regex: new RegExp(`^${regexStr}$`),
		paramNames,
		handler,
	});

	return router;
}

/**
 * Register a navigation guard.
 * Called before every navigation. If any guard returns false
 * (or a Promise that resolves to false), navigation is cancelled.
 * @param {(to: string, params: object) => boolean | Promise<boolean>} guardFn
 */
export function beforeEach(guardFn) {
	guards.push(guardFn);
}

/**
 * Register a route change listener.
 * @param {(route: string, params: object) => void} listener
 */
export function onChange(listener) {
	changeListeners.push(listener);
}

/* ── Navigation ───────────────────────────────────────────────────────────── */

/**
 * Navigate to a path (hash). Triggers route matching and guards.
 * @param {string} path - e.g. "/" or "/detail/abc-123"
 * @param {{ replace?: boolean }} [options]
 */
export async function navigate(path, options = {}) {
	// Normalize path — strip leading # and ensure starts with /
	let normalized = path.replace(/^#/, "");
	if (!normalized.startsWith("/")) normalized = "/" + normalized;

	const resolved = resolve(normalized);
	if (!resolved) return;

	const { route, params } = resolved;

	// Run guards
	for (const guard of guards) {
		try {
			const ok = await guard(route, params);
			if (ok === false) return;
		} catch {
			/* guard error — allow navigation */
		}
	}

	// Update hash
	if (options.replace) {
		window.history.replaceState(null, "", `#${normalized}`);
	} else {
		window.location.hash = normalized;
	}

	// Only dispatch if route changed
	if (currentRoute !== route) {
		currentRoute = route;
		dispatch(route, params);
	}
}

/**
 * Replace current history entry (no back-button entry).
 */
export function replace(path) {
	return navigate(path, { replace: true });
}

/**
 * Resolve current hash to a route handler.
 * @returns {{ route: string, params: object } | null}
 */
export function resolveCurrent() {
	const hash = window.location.hash.slice(1) || "/";
	return resolve(hash);
}

/**
 * Get the current route path.
 */
export function getCurrentRoute() {
	return currentRoute;
}

/* ── Internal ─────────────────────────────────────────────────────────────── */

/**
 * Match a path against registered routes.
 */
function resolve(path) {
	for (const route of routes) {
		const match = path.match(route.regex);
		if (match) {
			const params = {};
			route.paramNames.forEach((name, i) => {
				params[name] = decodeURIComponent(match[i + 1]);
			});
			return { route: route.pattern, params };
		}
	}
	console.warn(`[Router] No route matched: ${path}`);
	return null;
}

/**
 * Call handler and listeners for a matched route.
 */
function dispatch(route, params) {
	const resolved = resolve(window.location.hash.slice(1) || "/") || {
		route: "",
		params: {},
	};

	// Call the registered handler
	for (const r of routes) {
		if (r.pattern === resolved.route) {
			try {
				r.handler(resolved.params);
			} catch (e) {
				console.error("[Router] Handler error:", e);
			}
			break;
		}
	}

	// Notify listeners
	for (const listener of changeListeners) {
		try {
			listener(resolved.route, resolved.params);
		} catch {
			/* swallow */
		}
	}
}

/* ── Init ─────────────────────────────────────────────────────────────────── */

/**
 * Start listening to hash changes and fire initial route.
 */
export function start() {
	window.addEventListener("hashchange", () => {
		const resolved = resolve(window.location.hash.slice(1) || "/");
		if (resolved && resolved.route !== currentRoute) {
			currentRoute = resolved.route;
			dispatch(resolved.route, resolved.params);
		}
	});

	// Fire initial route
	const resolved = resolve(window.location.hash.slice(1) || "/");
	if (resolved) {
		currentRoute = resolved.route;
		dispatch(resolved.route, resolved.params);
	}
}

/* ── Chaining API ─────────────────────────────────────────────────────────── */

export const router = {
	on,
	beforeEach,
	onChange,
	navigate,
	replace,
	start,
	getCurrentRoute,
	resolveCurrent,
};
export default router;
